import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, X } from 'react-feather';
import { FeatureTag } from '../ui/feature-tag';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { featureTagApi } from '../../services/api';
import './FeatureTagSelector.css';

const FeatureTagSelector = ({ 
  projectId, 
  selectedTagId, 
  onTagSelect, 
  onTagCreate,
  disabled = false,
  placeholder = "Select feature tag...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch feature tags for the project
  const fetchTags = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const data = await featureTagApi.getByProject(projectId);
      setTags(data);
    } catch (error) {
      console.error('Error fetching feature tags:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [projectId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewTagName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedTag = tags.find(tag => tag.id === selectedTagId);

  const handleTagSelect = (tag) => {
    onTagSelect(tag.id);
    setIsOpen(false);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setCreating(true);
      const newTag = await featureTagApi.create({
        name: newTagName.trim(),
        project_id: projectId
      });

      setTags(prev => [...prev, newTag]);
      onTagSelect(newTag.id);
      setNewTagName('');
      setShowCreateForm(false);
      setIsOpen(false);
      
      if (onTagCreate) {
        onTagCreate(newTag);
      }
    } catch (error) {
      console.error('Error creating feature tag:', error);
      alert(error.response?.data?.error || 'Failed to create feature tag');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveTag = () => {
    onTagSelect(null);
  };

  return (
    <div className={`feature-tag-selector ${className}`} ref={dropdownRef}>
      {/* Selected tag display */}
      <div 
        className={`feature-tag-selector__trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedTag ? (
          <div className="feature-tag-selector__selected">
            <FeatureTag 
              name={selectedTag.name} 
              color={selectedTag.color}
              size="small"
              removable
              onRemove={handleRemoveTag}
            />
          </div>
        ) : (
          <span className="feature-tag-selector__placeholder">
            {placeholder}
          </span>
        )}
        <ChevronDown size={16} className={`feature-tag-selector__chevron ${isOpen ? 'rotated' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="feature-tag-selector__dropdown">
          {loading ? (
            <div className="feature-tag-selector__loading">Loading tags...</div>
          ) : (
            <>
              {/* Tag list */}
              <div className="feature-tag-selector__list">
                {tags.length === 0 ? (
                  <div className="feature-tag-selector__empty">
                    No feature tags found
                  </div>
                ) : (
                  tags.map(tag => (
                    <div
                      key={tag.id}
                      className={`feature-tag-selector__option ${selectedTagId === tag.id ? 'selected' : ''}`}
                      onClick={() => handleTagSelect(tag)}
                    >
                      <FeatureTag 
                        name={tag.name} 
                        color={tag.color}
                        size="small"
                      />
                      <span className="feature-tag-selector__task-count">
                        {tag.task_count} tasks
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Create new tag form */}
              {showCreateForm ? (
                <div className="feature-tag-selector__create-form">
                  <TextField>
                    <TextField.Input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateTag();
                        }
                      }}
                      placeholder="Enter tag name..."
                      autoFocus
                      maxLength={50}
                    />
                  </TextField>
                  <div className="feature-tag-selector__create-actions">
                    <Button 
                      onClick={handleCreateTag}
                      variant="primary" 
                      size="sm"
                      disabled={!newTagName.trim() || creating}
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="neutral" 
                      size="sm"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewTagName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="feature-tag-selector__create-trigger"
                  onClick={() => setShowCreateForm(true)}
                >
                  <Plus size={16} />
                  <span>Create new tag</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FeatureTagSelector;
